import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivationStart, Router } from '@angular/router';
import { AuthService } from '@app/core/authentication/auth.service';
import { NbIconLibraries } from '@nebular/theme';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { SocketService } from '@services/socket.service';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { DEFAULT_LANGUAGES } from './core/helpers/available-languages';
declare var gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [NgbTooltipConfig]
})
export class AppComponent implements OnInit, OnDestroy {
  private title: string = 'publisher-angular';
  private currentMetaTitleKey: string = '';
  private panelLang: string;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly translateService: TranslateService,
    private readonly iconLibraries: NbIconLibraries,
    private readonly socketService: SocketService,
    private readonly localStorageService: LocalStorageService,
    private readonly auth: AuthService,
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly tooltipConfig: NgbTooltipConfig
  ) {
    this.iconLibraries.registerFontPack('icomoon', {
      iconClassPrefix: 'i',
      packClass: 'i'
    });

    this.translateService.addLangs(Object.keys(DEFAULT_LANGUAGES));

    tooltipConfig.animation = false;
    tooltipConfig.tooltipClass = 'my-configurations-tooltip';
  }

  /**
   * @description Function to set site title
   * @param newTitle String with new site title
   */

  public setTitle(newTitle: string): void {
    this.titleService.setTitle(newTitle);
  }

  ngOnInit(): void {
    // Replace 'page_view' with an appropriate event name for your page
    gtag('event', 'page_view', {
      event_category: 'Navigation',
      event_label: 'Page Loaded'
    });
    if (this.auth.isLoggedIn()) {
      this.socketService.setupSocketConnection();
      this.setPanelLang();
    }

    if (this.translateService.store.translations[this.translateService.currentLang]) {
      this.getTitleFromRoute();
    }

    this.translateService.onLangChange
      .pipe(
        tap(() => {
          const translatedMetaTitle = this.translateService.instant('view_translations.' + this.currentMetaTitleKey);
          this.setTitle(translatedMetaTitle);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.customScrollbar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setPanelLang(): void {
    this.panelLang = this.localStorageService.getPanelLang();

    if (this.panelLang === null || !this.panelLang || this.panelLang === 'null') {
      this.panelLang = navigator.language.split('-')[0];
    }

    this.translateService.use(this.panelLang);
  }

  getTitleFromRoute(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof ActivationStart),
        tap((event: ActivationStart) => {
          const translationKey = event.snapshot.data.title ? event.snapshot.data.title : 'best_affiliate_network';
          this.currentMetaTitleKey = translationKey;

          const metaTitle = this.translateService.instant('view_translations.' + translationKey);
          this.setTitle(metaTitle);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  customScrollbar(): void {
    if (!/Win/i.test(navigator.platform)) {
      return;
    }
    document.querySelector('head')!.innerHTML += `
      <style>
      :root {
        --gray-dark: #A8B4C1;
        --gray: #dbdce0;
      }

    * {
      scrollbar-width: thin;
      scrollbar-color: var(--gray) transparent;
    }

    ::-webkit-scrollbar-track {
      background-color: transparent !important;
    }

    ::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      background-color: rgba(white, 0) !important;
    }

    ::-webkit-scrollbar-thumb {
      background-color: var(--gray);
      border-radius: 16px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background-color: var(--gray-dark);
    }
      </style>
    `;
  }
}
